import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Fab, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import LoadingView from '../components/LoadingView';
import { useNotification } from '../providers/NotificationProvider';
import { useMarketSymbols } from '../hooks/useMarketSymbols';
import { useMarketAnalysis, MARKET_RANGES, type MarketRange } from '../hooks/useMarketAnalysis';
import MarketSummaryCards from '../components/marketAnalysis/MarketSummaryCards';
import MarketPriceChart from '../components/marketAnalysis/MarketPriceChart';
import { MarketRsiChart, MarketVolumeChart } from '../components/marketAnalysis/MarketSubCharts';
import MarketSymbolDialog from '../components/marketAnalysis/MarketSymbolDialog';

/**
 * 為替・株式分析ページ。
 *
 * 銘柄マスタ (mst_market_symbol) に登録した USD/JPY・日本株の日足を Cloud SQL から読み、
 * 終値・移動平均・ボリンジャーバンド・RSI と、ARIMA による 30 営業日先の統計予測を表示する。
 * 日足は火〜土 07:00 の Scheduler が自動取込。「最新取得」で選択中の銘柄を手動で取り直せる。
 */
export default function MarketAnalysis() {
  const notify = useNotification();
  const sym = useMarketSymbols();
  const ma = useMarketAnalysis();
  const [dialogOpen, setDialogOpen] = useState(false);

  const activeSymbols = useMemo(() => sym.symbols.filter((s) => s.is_active), [sym.symbols]);

  // 選択中の銘柄が無効化・削除されたら先頭の有効銘柄へ切り替える
  useEffect(() => {
    if (sym.loading || activeSymbols.length === 0) return;
    if (!activeSymbols.some((s) => s.symbol_code === ma.symbolCode)) {
      ma.setSymbolCode(activeSymbols[0].symbol_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym.loading, activeSymbols]);

  const symbol = ma.chart?.symbol;
  const currency = symbol?.currency ?? 'JPY';
  const displayName = symbol?.display_name ?? ma.symbolCode;
  const isFx = symbol?.asset_type === 'fx';
  const hasVolume = !isFx && (ma.chart?.rows ?? []).some((r) => (r.volume ?? 0) > 0);

  const handleSync = async () => {
    try {
      const rows = await ma.runSync('1mo');
      notify.success(`${displayName} の直近 1 か月分 (${rows} 日) を取り直しました`);
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '最新データの取得に失敗しました');
    }
  };

  const busy = ma.loading || ma.syncing;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: PAGE_FILL_HEIGHT, boxSizing: 'border-box' }}>
      <Box sx={{ width: '94%', mt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* 見出し + 銘柄・期間セレクタ + 操作ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
              <Icons.CandlestickChart /> 為替・株式分析
              <span style={{ fontSize: '18px', fontWeight: 500, color: '#64748b' }}>- {displayName}</span>
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              日足 (Yahoo Finance) を毎営業日の翌朝に取り込み、テクニカル指標と ARIMA による統計予測を表示します。予測は参考値であり投資判断の根拠にはなりません。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <TextField
              select
              size="small"
              label="銘柄"
              value={activeSymbols.some((s) => s.symbol_code === ma.symbolCode) ? ma.symbolCode : ''}
              onChange={(e) => ma.setSymbolCode(e.target.value)}
              disabled={busy || activeSymbols.length === 0}
              sx={{ minWidth: 220, '& .MuiInputBase-input': { fontSize: '13px', py: 0.9 } }}
            >
              {activeSymbols.map((s) => (
                <MenuItem key={s.symbol_code} value={s.symbol_code} sx={{ fontSize: '13px' }}>
                  {s.display_name} <span style={{ color: '#94a3b8', marginLeft: 6, fontFamily: 'monospace' }}>{s.symbol_code}</span>
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={ma.range}
              onChange={(_, v: MarketRange | null) => { if (v) ma.setRange(v); }}
              disabled={busy}
              sx={{ '& .MuiToggleButton-root': { px: 1.2, py: 0.5, fontSize: '12px', fontWeight: 700 } }}
            >
              {MARKET_RANGES.map((r) => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
            </ToggleButtonGroup>
            {ma.chart?.summary && (
              <Chip size="small" label={`最終取込 ${ma.chart.summary.as_of}`} sx={{ fontSize: '11px', height: '22px' }} />
            )}
            <Fab variant="extended" size="small" onClick={handleSync} disabled={busy || !ma.symbolCode} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Sync sx={{ mr: 1 }} /> 最新取得
            </Fab>
            <Fab variant="extended" size="small" onClick={() => setDialogOpen(true)} disabled={sym.loading} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Settings sx={{ mr: 1 }} /> 銘柄管理
            </Fab>
          </Box>
        </Box>

        {(ma.error || sym.error) && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{ma.error || sym.error}</Alert>
        )}
        {ma.forecast?.warning && !ma.error && (
          <Alert severity="info" sx={{ width: '100%', borderRadius: '8px', py: 0 }}>予測: {ma.forecast.warning}</Alert>
        )}

        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {ma.chart && ma.chart.rows.length > 0 && (
            <>
              <MarketSummaryCards summary={ma.chart.summary} currency={currency} />
              <MarketPriceChart rows={ma.chart.rows} forecast={ma.forecast} currency={currency} displayName={displayName} />
              <Box sx={{ display: 'grid', gridTemplateColumns: hasVolume ? { xs: '1fr', md: '1fr 1fr' } : '1fr', gap: 2 }}>
                {hasVolume && <MarketVolumeChart rows={ma.chart.rows} />}
                <MarketRsiChart rows={ma.chart.rows} />
              </Box>
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', px: 0.5 }}>
                予測は直近 {ma.forecast?.lookback ?? 0} 営業日の終値に {ma.forecast?.method ?? 'ARIMA'} を当てはめた統計的推定で、帯は 80% 信頼区間です。将来日付は土日のみ除外し、祝日は考慮していません。
              </Typography>
            </>
          )}

          {!busy && !ma.error && (!ma.chart || ma.chart.rows.length === 0) && (
            <Box sx={{ mt: 6, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
              <Typography color="textSecondary">
                {activeSymbols.length === 0 ? '銘柄が登録されていません。「銘柄管理」から追加してください。' : '日足データがありません。「最新取得」で取り込んでください。'}
              </Typography>
            </Box>
          )}

          {busy && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.65)', borderRadius: '12px', backdropFilter: 'blur(1px)', minHeight: 240 }}>
              <LoadingView message={ma.syncing ? '最新の日足を取得中...' : 'チャートを作成中...'} height="auto" size={50} />
            </Box>
          )}
        </Box>
      </Box>

      <MarketSymbolDialog
        open={dialogOpen}
        symbols={sym.symbols}
        onClose={() => setDialogOpen(false)}
        onSave={sym.save}
        onRemove={sym.remove}
      />
    </Box>
  );
}
